import React, { useCallback, useEffect, useState } from "react";
import {
  X,
  Loader2,
  Save,
  User,
  Phone,
  Mail,
  Building2,
  GraduationCap,
  MapPin,
  BookOpen,
  Smartphone,
  Wallet,
  Sparkles,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Select from "react-select";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  mapBranchCustomersToOptions,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import customSelectStyles from "../../../components/ui/customSelectStyles";
import { toTitleCaseVietnamese } from "../../../utils/textUtils";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import {
  createTeacherAccount,
  updateTeacherAccount,
  getTeacherAccountById,
} from "../../../api/crm/teacherAccounts/teacherAccountInfo";
import { showSuccessToast, showErrorToast } from "../../../components/ConfirmDialog";
import EmployeeDocumentAccessPanel from "../../hrm/employees/EmployeeDocumentAccessPanel";
import { VIETNAM_BANKS } from "../../../data/vietnamBanks";
import { startTeacherAccountFormTour } from "./teacherAccountFormTour";

/* ================= COMPONENT PHỤ (giống EmployeeFormModal) ================= */
const Section = ({ icon: Icon, title, subTitle, children }) => (
  <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm space-y-4">
    <div className="flex items-center gap-3 pb-2 border-b border-gray-50">
      <div className="p-2 bg-red-50 rounded-lg">
        <Icon className="w-5 h-5 text-red-600" />
      </div>
      <div>
        <h3 className="text-sm font-bold text-gray-800 uppercase tracking-tight">
          {title}
        </h3>
        {subTitle && <p className="text-xs text-gray-400">{subTitle}</p>}
      </div>
    </div>
    <div className="pt-2">{children}</div>
  </div>
);

const FloatLabel = ({ label, required, children }) => (
  <div className="relative">
    <span className="text-[10px] uppercase font-bold text-gray-400 absolute -top-2 left-3 bg-white px-1 z-10">
      {label}
      {required ? <span className="text-red-500"> *</span> : null}
    </span>
    {children}
  </div>
);

const INPUT_CLASS =
  "w-full border rounded-xl px-4 py-3 border-gray-200 outline-none focus:ring-2 focus:ring-red-100 focus:border-red-400 transition-all text-sm text-gray-800 font-medium";

/** Email dùng đăng nhập APP (bắt buộc khi tạo / khi đổi email) */
const isValidPersonalLoginEmail = (value) => {
  const s = String(value || "").trim();
  if (!s) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
};

const INITIAL_FORM = {
  branch_id: "",
  partner_customer_id: "",
  name: "",
  email: "",
  phone: "",
  birthdate: "",
  gender: "",
  remark: "",
  is_active: true,
  monthly_limit: 5,
  bonus_balance: 0,
  doc_quota_exempt: false,
  max_teacher_devices: 2,
  payroll_bank_code: "",
  payroll_bank_account_number: "",
  payroll_bank_account_name: "",
};

const GENDER_OPTIONS = [
  { value: "male", label: "Nam" },
  { value: "female", label: "Nữ" },
];

/** Chính sách cố định: 1 máy tính + 1 điện thoại */
const DEVICE_POLICY_TEXT =
  "Mỗi tài khoản chỉ đăng nhập trên 1 máy tính và 1 điện thoại (không 2 thiết bị cùng loại). Thiết bị mới cùng loại cần admin duyệt hoặc gỡ thiết bị cũ cùng loại trước.";

const TeacherAccountFormModal = ({ isOpen, onClose, employeeId, onSaved }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [branches, setBranches] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [form, setForm] = useState(INITIAL_FORM);

  const isEdit = !!employeeId;
  const handleQuickTour = useCallback(() => {
    window.setTimeout(() => startTeacherAccountFormTour({ isEdit }), 80);
  }, [isEdit]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const b = await getAllBranches_Array();
        const list = Array.isArray(b) ? b : b?.data || b?.items || [];
        setBranches(
          list.map((x) => ({ value: x.id, label: x.name }))
        );
      } catch {
        setBranches([]);
      }
    })();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !form.branch_id) {
      setCustomers([]);
      return;
    }
    (async () => {
      try {
        const res = await getCustomersByBranch(form.branch_id);
        const items = res?.items || res || [];
        const arr = Array.isArray(items) ? items : [];
        setCustomers(mapBranchCustomersToOptions(arr));
      } catch {
        setCustomers([]);
      }
    })();
  }, [isOpen, form.branch_id]);

  useEffect(() => {
    if (!isOpen) return;
    if (!employeeId) {
      setForm(INITIAL_FORM);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const row = await getTeacherAccountById(employeeId);
        setForm({
          branch_id: row.branch_id || "",
          partner_customer_id: row.partner_customer_id || "",
          name: row.name || "",
          email: row.email || "",
          phone: row.phone || "",
          birthdate: row.birthdate ? String(row.birthdate).slice(0, 10) : "",
          gender: row.gender || "",
          remark: row.remark || "",
          is_active: row.is_active !== false,
          monthly_limit: 5,
          bonus_balance: 0,
          doc_quota_exempt: false,
          max_teacher_devices: 2,
          payroll_bank_code: row.payroll_bank_code || "",
          payroll_bank_account_number: row.payroll_bank_account_number || "",
          payroll_bank_account_name: row.payroll_bank_account_name || "",
        });
      } catch {
        showErrorToast("Không tải được dữ liệu.");
        onClose?.();
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, employeeId, onClose]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSave = async () => {
    if (
      !form.branch_id ||
      !form.partner_customer_id ||
      !form.name?.trim() ||
      !form.email?.trim()
    ) {
      showErrorToast(
        "Vui lòng điền chi nhánh, trường (khách hàng), họ tên và email cá nhân đăng nhập APP."
      );
      return;
    }
    if (!isValidPersonalLoginEmail(form.email)) {
      showErrorToast(
        "Email cá nhân không hợp lệ. Đây là tài khoản đăng nhập bắt buộc — vui lòng nhập đúng định dạng email."
      );
      return;
    }
    setSaving(true);
    try {
      const payload = {
        branch_id: form.branch_id,
        partner_customer_id: form.partner_customer_id,
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone?.trim() || null,
        birthdate: form.birthdate || null,
        gender: form.gender || null,
        remark: form.remark || null,
        is_active: form.is_active,
        monthly_limit: Math.min(1000, Math.max(0, parseInt(form.monthly_limit, 10) || 5)),
        bonus_balance: Math.min(100000, Math.max(0, parseInt(form.bonus_balance, 10) || 0)),
        doc_quota_exempt: !!form.doc_quota_exempt,
        max_teacher_devices: 2,
        payroll_bank_code: form.payroll_bank_code?.trim() || null,
        payroll_bank_account_number: form.payroll_bank_account_number?.trim() || null,
        payroll_bank_account_name: form.payroll_bank_account_name?.trim() || null,
      };
      if (isEdit) {
        await updateTeacherAccount(employeeId, payload);
        showSuccessToast("Đã cập nhật tài khoản.");
      } else {
        await createTeacherAccount(payload);
        showSuccessToast("Đã tạo tài khoản. Mật khẩu đã gửi qua email.");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.message ||
        "Không lưu được.";
      showErrorToast(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[110] bg-gray-900/60 backdrop-blur-[2px]"
            onClick={() => onClose?.()}
          />
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="fixed inset-0 z-[120] flex items-center justify-center p-4 pointer-events-none"
          >
            <div className="bg-slate-50 rounded-3xl shadow-2xl w-full max-w-5xl flex flex-col overflow-hidden pointer-events-auto border border-white">
              {/* HEADER */}
              <div
                className="bg-white px-8 py-5 flex justify-between items-center border-b border-gray-100"
                data-tour="teacher-account-form-header"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-red-600 rounded-2xl flex items-center justify-center shadow-lg shadow-red-200">
                    <GraduationCap className="text-white w-7 h-7" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900 leading-none">
                      {isEdit
                        ? "Cập nhật tài khoản giáo viên đối tác"
                        : "Thêm tài khoản giáo viên đối tác"}
                    </h2>
                    <p className="text-sm text-gray-400 mt-1">
                      {isEdit
                        ? "Gắn với trường học (khách hàng CRM) và chi nhánh quản lý."
                        : "Khởi tạo đăng nhập cho giáo viên trường — cùng hồ sơ nhân sự, phân loại GV đối tác."}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={handleQuickTour}
                    className="inline-flex items-center gap-2 px-3 py-2 rounded-xl border border-red-200 bg-red-50 text-red-700 hover:bg-red-100 transition-colors text-[11px] font-bold uppercase tracking-wide"
                  >
                    <Sparkles className="w-4 h-4" />
                    Hướng dẫn nhanh
                  </button>
                  <button
                    type="button"
                    onClick={() => onClose?.()}
                    className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400"
                  >
                    <X size={24} />
                  </button>
                </div>
              </div>

              {/* BODY */}
              <div className="p-8 grid grid-cols-1 md:grid-cols-2 gap-6 overflow-y-auto max-h-[75vh] custom-scrollbar">
                {loading ? (
                  <div className="md:col-span-2 flex justify-center py-20">
                    <Loader2 className="w-10 h-10 animate-spin text-red-600" />
                  </div>
                ) : (
                  <>
                    {/* CỘT TRÁI */}
                    <div className="space-y-6">
                      <div data-tour="teacher-account-form-personal">
                        <Section
                          icon={User}
                          title="Thông tin cá nhân"
                          subTitle="Định danh đăng nhập hệ thống"
                        >
                        <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200">
                          <div className="w-20 h-20 rounded-2xl bg-red-50 flex items-center justify-center ring-4 ring-white shadow-md">
                            <GraduationCap className="w-10 h-10 text-red-600" />
                          </div>
                          <div className="flex-1 space-y-2">
                            <p className="text-xs font-bold text-gray-500 uppercase tracking-tight">
                              Giáo viên đối tác
                            </p>
                            <p className="text-xs text-gray-500 leading-relaxed">
                              Tài khoản dùng chung nền nhân sự; phân biệt bởi trường (khách hàng) và
                              chi nhánh.
                            </p>
                          </div>
                        </div>

                        <div className="space-y-4">
                          <FloatLabel label="Họ và tên" required>
                            <input
                              name="name"
                              value={form.name}
                              onChange={handleChange}
                              onBlur={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  name: toTitleCaseVietnamese(e.target.value),
                                }))
                              }
                              placeholder="NGUYỄN VĂN A"
                              className={`${INPUT_CLASS} font-black text-base`}
                            />
                          </FloatLabel>

                          <div className="grid grid-cols-2 gap-3">
                            <FloatLabel label="Ngày sinh">
                              <input
                                name="birthdate"
                                type="date"
                                value={form.birthdate}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                              />
                            </FloatLabel>
                            <FloatLabel label="Giới tính">
                              <Select
                                styles={customSelectStyles}
                                options={GENDER_OPTIONS}
                                value={
                                  GENDER_OPTIONS.find((o) => o.value === form.gender) || null
                                }
                                onChange={(opt) =>
                                  setForm((f) => ({ ...f, gender: opt?.value || "" }))
                                }
                                placeholder="— Chọn —"
                                isClearable
                                {...modalSelectPortalProps}
                              />
                            </FloatLabel>
                          </div>

                          <FloatLabel label="Email cá nhân (đăng nhập APP)" required>
                            <div className="relative">
                              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                name="email"
                                type="email"
                                autoComplete="email"
                                value={form.email}
                                onChange={handleChange}
                                placeholder="email.canhan@gmail.com"
                                className={`${INPUT_CLASS} pl-10`}
                              />
                            </div>
                            <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                              Bắt buộc: đây là tài khoản đăng nhập ứng dụng (cùng giá trị với email trong hồ
                              sơ nhân sự). Mật khẩu gửi qua địa chỉ này khi tạo mới.
                            </p>
                          </FloatLabel>

                          <FloatLabel label="Số điện thoại">
                            <div className="relative">
                              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                              <input
                                name="phone"
                                type="tel"
                                value={form.phone}
                                onChange={handleChange}
                                placeholder="09xx..."
                                className={`${INPUT_CLASS} pl-10`}
                              />
                            </div>
                          </FloatLabel>
                        </div>
                        </Section>
                      </div>

                      <div data-tour="teacher-account-form-payroll">
                        <Section
                          icon={Wallet}
                          title="Tài khoản nhận lương"
                          subTitle="Ngân hàng Việt Nam — dùng chi trả lương"
                        >
                        <div className="space-y-4">
                          <FloatLabel label="Ngân hàng">
                            <Select
                              styles={customSelectStyles}
                              options={VIETNAM_BANKS}
                              value={
                                VIETNAM_BANKS.find((b) => b.value === form.payroll_bank_code) ||
                                null
                              }
                              onChange={(opt) =>
                                setForm((f) => ({
                                  ...f,
                                  payroll_bank_code: opt?.value || "",
                                }))
                              }
                              placeholder="— Chọn ngân hàng —"
                              isClearable
                              {...modalSelectPortalProps}
                            />
                          </FloatLabel>
                          <FloatLabel label="Số tài khoản">
                            <input
                              name="payroll_bank_account_number"
                              value={form.payroll_bank_account_number}
                              onChange={handleChange}
                              placeholder="Số tài khoản"
                              autoComplete="off"
                              className={INPUT_CLASS}
                            />
                          </FloatLabel>
                          <FloatLabel label="Tên tài khoản">
                            <input
                              name="payroll_bank_account_name"
                              value={form.payroll_bank_account_name}
                              onChange={handleChange}
                              onBlur={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  payroll_bank_account_name: toTitleCaseVietnamese(
                                    e.target.value
                                  ),
                                }))
                              }
                              placeholder="Chủ tài khoản (in hoa không dấu hoặc theo ngân hàng)"
                              className={INPUT_CLASS}
                            />
                          </FloatLabel>
                        </div>
                        </Section>
                      </div>

                      <Section
                        icon={MapPin}
                        title="Ghi chú"
                        subTitle="Thông tin bổ sung (tuỳ chọn)"
                      >
                        <FloatLabel label="Ghi chú">
                          <textarea
                            name="remark"
                            value={form.remark}
                            onChange={handleChange}
                            rows={3}
                            className={`${INPUT_CLASS} resize-none min-h-[88px]`}
                          />
                        </FloatLabel>
                      </Section>
                    </div>

                    {/* CỘT PHẢI */}
                    <div className="space-y-6">
                      {!isEdit && (
                        <div data-tour="teacher-account-form-org">
                          <Section
                            icon={Building2}
                            title="Đối tác & tổ chức"
                            subTitle="Chi nhánh và khách hàng (trường học)"
                          >
                          <div className="space-y-4">
                            <FloatLabel label="Chi nhánh" required>
                              <Select
                                styles={customSelectStyles}
                                options={branches}
                                value={branches.find((b) => b.value === form.branch_id) || null}
                                onChange={(opt) =>
                                  setForm((f) => ({
                                    ...f,
                                    branch_id: opt?.value || "",
                                    partner_customer_id: "",
                                  }))
                                }
                                placeholder="Chọn chi nhánh..."
                                {...modalSelectPortalProps}
                              />
                            </FloatLabel>

                            <FloatLabel label="Trường (khách hàng CRM)" required>
                              <Select
                                styles={customerInModalSelectStyles}
                                isDisabled={!form.branch_id}
                                options={customers}
                                value={
                                  customers.find((c) => c.value === form.partner_customer_id) ||
                                  null
                                }
                                onChange={(opt) =>
                                  setForm((f) => ({
                                    ...f,
                                    partner_customer_id: opt?.value || "",
                                  }))
                                }
                                placeholder={
                                  form.branch_id ? "Chọn trường..." : "Chọn chi nhánh trước..."
                                }
                                formatOptionLabel={formatCustomerOptionLabel}
                                filterOption={filterCustomerOption}
                                {...modalSelectPortalProps}
                              />
                            </FloatLabel>
                          </div>
                          </Section>
                        </div>
                      )}

                      {!isEdit && (
                        <div data-tour="teacher-account-form-docquota">
                          <Section
                            icon={BookOpen}
                            title="Lượt tải / xem tài liệu"
                            subTitle="Thiết lập ngay khi tạo mới hoặc cập nhật tài khoản"
                          >
                          <div className="grid grid-cols-2 gap-3">
                            <FloatLabel label="Hạn mức tháng (0-1000)">
                              <input
                                name="monthly_limit"
                                type="number"
                                min={0}
                                max={1000}
                                value={form.monthly_limit}
                                onChange={handleChange}
                                className={INPUT_CLASS}
                              />
                            </FloatLabel>
                            <div>
                              <FloatLabel label="Lượt bonus ban đầu">
                                <input
                                  name="bonus_balance"
                                  type="number"
                                  min={0}
                                  max={100000}
                                  value={form.bonus_balance}
                                  onChange={handleChange}
                                  className={INPUT_CLASS}
                                />
                              </FloatLabel>
                              <p className="text-[11px] text-gray-500 mt-1.5 leading-relaxed">
                                Số lượt cộng thêm ngoài hạn mức tháng. Ví dụ hạn mức tháng = 5, bonus = 10 thì
                                giáo viên có tổng 15 lượt tải/xem trong tháng hiện tại.
                              </p>
                            </div>
                          </div>

                          <label className="mt-3 flex items-center gap-3 p-3 rounded-xl border border-gray-100 bg-gray-50/60 cursor-pointer">
                            <input
                              type="checkbox"
                              name="doc_quota_exempt"
                              checked={!!form.doc_quota_exempt}
                              onChange={handleChange}
                              className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4"
                            />
                            <span className="text-sm font-bold text-gray-700 uppercase tracking-tight">
                              Miễn trừ hạn mức tài liệu
                            </span>
                          </label>

                          <div className="rounded-xl border border-violet-100 bg-violet-50/60 p-3">
                            <p className="text-[11px] font-bold uppercase tracking-wide text-violet-800">
                              Thiết bị đăng nhập APP
                            </p>
                            <p className="mt-1 text-[11px] leading-relaxed text-violet-900/80">
                              {DEVICE_POLICY_TEXT}
                            </p>
                          </div>
                          </Section>
                        </div>
                      )}

                      {isEdit && (
                        <>
                          <div
                            className="p-6 bg-amber-50 border border-amber-100 rounded-3xl"
                            data-tour="teacher-account-form-status"
                          >
                            <label className="flex items-center gap-3 cursor-pointer">
                              <input
                                type="checkbox"
                                name="is_active"
                                checked={!!form.is_active}
                                onChange={handleChange}
                                className="rounded border-gray-300 text-red-600 focus:ring-red-500 w-4 h-4"
                              />
                              <span className="text-sm font-bold text-amber-900 uppercase tracking-tight">
                                Tài khoản đang hoạt động
                              </span>
                            </label>
                            <p className="text-xs text-amber-800/80 mt-2 leading-relaxed">
                              Bỏ chọn để khóa đăng nhập (trạng thái tương tự nhân viên ngưng sử dụng).
                            </p>
                          </div>

                          <div data-tour="teacher-account-form-devices">
                            <Section
                              icon={Smartphone}
                              title="Thiết bị đăng nhập APP"
                              subTitle="1 máy tính + 1 điện thoại — quản lý tại chi tiết / Đổi thiết bị"
                            >
                            <p className="text-[11px] text-gray-500 leading-relaxed">
                              {DEVICE_POLICY_TEXT}
                            </p>
                            </Section>
                          </div>

                          <EmployeeDocumentAccessPanel employeeId={employeeId} />
                        </>
                      )}

                      {!isEdit && (
                        <div className="p-6 bg-blue-50 border border-blue-100 rounded-3xl">
                          <div className="flex gap-4">
                            <div className="p-2 bg-blue-100 rounded-xl h-fit">
                              <Mail className="text-blue-600" size={24} />
                            </div>
                            <p className="text-xs text-blue-700 leading-relaxed font-medium">
                              Sau khi lưu, hệ thống gửi <b>mật khẩu tạm</b> tới{" "}
                              <b>email cá nhân</b> vừa nhập (tài khoản đăng nhập APP). Giáo viên có thể
                              đổi mật khẩu trong ứng dụng.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* FOOTER */}
              <div
                className="bg-white px-8 py-5 flex justify-end gap-4 border-t border-gray-100 shadow-[0_-4px_20px_rgba(0,0,0,0.03)]"
                data-tour="teacher-account-form-footer"
              >
                <button
                  type="button"
                  onClick={() => onClose?.()}
                  className="px-8 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-50 border border-gray-200 transition-all uppercase text-xs tracking-widest"
                >
                  Huỷ bỏ
                </button>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || loading}
                  className="px-10 py-2.5 rounded-xl bg-red-600 text-white font-bold flex items-center gap-2 hover:bg-red-700 hover:shadow-lg hover:shadow-red-200 active:scale-95 transition-all disabled:opacity-60 uppercase text-xs tracking-widest"
                >
                  {saving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save size={16} />
                  )}
                  {saving ? "Đang xử lý..." : "Lưu hồ sơ"}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default TeacherAccountFormModal;
